import { Component, Input, OnInit } from '@angular/core';
import { NgbModal } from '@ng-bootstrap/ng-bootstrap';
import { ToastrService } from 'ngx-toastr';
import { of } from 'rxjs';
import { catchError, tap } from 'rxjs/operators';
import Swal from 'sweetalert2';
import { EmployeesModel } from 'src/app/model/employees.model';
import { CatEmployeeJobModel } from 'src/app/model/cat-employee-job.model';
import { CatCategoryPromoterModel } from 'src/app/model/cat-category-promoter.model';
import { PromotorCategoryAssignmentModel } from 'src/app/model/promotor-category-assignment.model';
import { CatEmployeeJobService } from 'src/app/services/cat-employee-job.service';
import { EmployeesService } from 'src/app/services/employees.service';
import { CatCategoryPromoterService } from 'src/app/services/cat-category-promoter.service';
import { PromotorCategoryAssignmentService } from 'src/app/services/promotor-category-assignment.service';
import { CatComponentService } from 'src/app/services/cat-component.service';
import { AuthService } from 'src/app/services/auth.service';

@Component({
  selector: 'app-modal-new-employee',
  templateUrl: './modal-new-employee.component.html',
  styleUrls: ['./modal-new-employee.component.css']
})
export class ModalNewEmployeeComponent implements OnInit {
  @Input() employee: EmployeesModel;
  @Input() mode: string;

  // estado pagina
  public viewStatus = 1;
  public employeeJobCatalog: CatEmployeeJobModel[] = [];

  // mascaras
  public customPatterns = {
    'A': { pattern: new RegExp('[a-zA-Z0-9ñÑ ]') },
    'C': { pattern: new RegExp('[a-zA-Z0-9áéíóúÁÉÍÓÚüÜñÑ,._:%\/() \-]') },
    'N': { pattern: new RegExp('[0-9]') }
  };

  // ── Asignación manual de categoría (solo visible con permiso, y solo editando un empleado ya guardado) ──
  public canAssignCategoryManual = false;
  public promoterCategoryCatalog: CatCategoryPromoterModel[] = [];
  public categoryAssignments: PromotorCategoryAssignmentModel[] = [];
  public loadingAssignments = false;
  public showAssignmentForm = false;
  public assignmentForm: PromotorCategoryAssignmentModel = {};

  ////////////////
  constructor(
    private employeesService: EmployeesService,
    private catEmployeeJobService: CatEmployeeJobService,
    private catCategoryPromoterService: CatCategoryPromoterService,
    private assignmentService: PromotorCategoryAssignmentService,
    private catComponentService: CatComponentService,
    private auth: AuthService,
    private modalService: NgbModal,
    private toastr: ToastrService
  ) { }
  ngOnInit() {
    this.getCatalogs();
    this.checkManualCategoryPermission();
  }

  //
  toggleActive = () => this.employee.iActivo = !this.employee.iActivo;
  isValid = () => {
    let valid = true;
    let errors = [];
    if (this.idEmpleadoError()) errors.push('Necesita capturar el número de empleado.');
    if (this.nombreError()) errors.push('Necesita capturar el nombre.');
    if (this.apellidoPaternoError()) errors.push('Necesita capturar el apellido paterno.');
    if (this.employeeJobError()) errors.push('Necesita seleccionar un puesto.');
    if (errors.length > 0) {
      valid = false;
      this.toastr.error(errors.join('<br>'), '¡Atención!', { enableHtml: true });
    }
    return valid;
  }
  save = () => {
    if (this.isValid()) {
      // mostrar spinner
      this.viewStatus = 0;
      // normalizar informacion
      this.employee.vIdempleado = this.employee.vIdempleado.toUpperCase();
      this.employee.vNombre = this.employee.vNombre.toUpperCase();
      this.employee.vApellidoPaterno = this.employee.vApellidoPaterno.toUpperCase();
      this.employee.vApellidoMaterno = this.employee.vApellidoMaterno ? this.employee.vApellidoMaterno.toUpperCase() : '';
      this.employee.vNombreEmpleado = [this.employee.vNombre, this.employee.vApellidoPaterno, this.employee.vApellidoMaterno]
        .filter(part => !!part).join(' ');
      let userName = localStorage.getItem('com_userName');
      // enviar informacion
      this.employeesService.save(this.employee, userName)
        .pipe(
          tap((resp: any) => {
            if (resp && resp.codeHttpStatus === 200) {
              if (this.mode === 'create') {
                // La asignación manual de categoría solo se habilita hasta que el empleado ya existe:
                // se pasa a modo edición en el mismo modal en vez de cerrarlo.
                this.mode = 'edit';
                if (resp.data) Object.assign(this.employee, resp.data);
                this.viewStatus = 1;
                this.toastr.success('El empleado se guardó con éxito. Ya puedes asignarle una categoría manual si aplica.');
                if (this.canAssignCategoryManual) this.loadAssignments();
              } else {
                this.modalService.dismissAll('success');
                this.toastr.success('El empleado se guardó con éxito.');
              }
            } else {
              this.viewStatus = 1;
              this.toastr.error(resp && resp.messageError ? resp.messageError : 'Ocurrió un error al intentar guardar el empleado.');
            }
          }),
          catchError(error => {
            this.viewStatus = 1;
            return of(this.toastr.error(error.error && error.error.message ? error.error.message : 'Ocurrió un error al intentar guardar el empleado.'));
          })
        )
        .subscribe();
    }
  }
  close = () => this.modalService.dismissAll();

  // HELPERS
  idEmpleadoError = () => !this.employee.vIdempleado || this.employee.vIdempleado.trim() === '';
  nombreError = () => !this.employee.vNombre || this.employee.vNombre.trim() === '';
  apellidoPaternoError = () => !this.employee.vApellidoPaterno || this.employee.vApellidoPaterno.trim() === '';
  employeeJobError = () => !this.employee.iEmployeeJobId;

  // el puesto actual del empleado nunca se deshabilita, aunque esté inactivo, para no perder la selección
  isJobDisabled = (job: CatEmployeeJobModel): boolean => {
    return !job.activeId && job.employeeJobId !== this.employee.iEmployeeJobId;
  }
  currentJobIsInactive = (): boolean => {
    const current = this.employeeJobCatalog.find(j => j.employeeJobId === this.employee.iEmployeeJobId);
    return !!current && !current.activeId;
  }

  // INFO
  getCatalogs = () => {
    this.catEmployeeJobService.getAll()
      .pipe(
        tap((resp: any) => {
          // Se muestran todos (activos e inactivos); los inactivos se deshabilitan en el template
          // salvo que sean el puesto actual del empleado.
          this.employeeJobCatalog = resp.data || [];
          this.viewStatus = 1;
        }),
        catchError(error => {
          this.viewStatus = -1;
          return of(this.toastr.error('Ocurrió un error al leer el catálogo de puestos.'));
        })
      )
      .subscribe();
  }

  // ── Asignación manual de categoría ──────────────────────────────

  checkManualCategoryPermission = () => {
    this.catComponentService.getElementsForComponent().subscribe((elementGrant: any) => {
      if (!elementGrant || !elementGrant.data) return;
      elementGrant.data
        .filter((el: any) => el.component === 'app-cat-employees' && this.auth.getUserRoles().includes(el.role))
        .forEach((el: any) => {
          if (el.description === 'asignacion-categoria-manual') {
            this.canAssignCategoryManual = !!el.active;
            if (this.canAssignCategoryManual && this.mode === 'edit') {
              this.loadCategoryCatalog();
              this.loadAssignments();
            }
          }
        });
    });
  }

  loadCategoryCatalog = () => {
    this.catCategoryPromoterService.getAll()
      .pipe(
        tap((resp: any) => {
          this.promoterCategoryCatalog = (resp.data || []).filter((c: CatCategoryPromoterModel) => !!c.activeId);
        }),
        catchError(() => of(this.toastr.error('Ocurrió un error al leer el catálogo de categorías.')))
      )
      .subscribe();
  }

  // El backend identifica al usuario autenticado por su login (com_userId, ej. "comisiones"),
  // nunca por el nombre completo visible (com_userName) ni por el rol (com_role).
  private getLoginUserId = (): string | null => {
    const loginUserId = localStorage.getItem('com_userId') || localStorage.getItem('com_employeeId');
    return loginUserId ? encodeURIComponent(loginUserId) : null;
  }

  loadAssignments = () => {
    if (!this.employee.vIdempleado) return;
    const userName = this.getLoginUserId();
    if (!userName) {
      this.toastr.error('No fue posible identificar al usuario autenticado.');
      return;
    }
    this.loadingAssignments = true;
    this.assignmentService.getByEmployee(this.employee.vIdempleado, userName)
      .pipe(
        tap((resp: any) => {
          this.categoryAssignments = resp.data || [];
          this.loadingAssignments = false;
        }),
        catchError((error: any) => {
          this.loadingAssignments = false;
          const message = error && error.error && error.error.messageError;
          return of(this.toastr.error(message || 'No fue posible leer las asignaciones de categoría del empleado.'));
        })
      )
      .subscribe();
  }

  categoryName = (promoterCategoryId: number): string => {
    const cat = this.promoterCategoryCatalog.find(c => c.promoterCategoryId === promoterCategoryId);
    return cat ? cat.description : String(promoterCategoryId);
  }

  openAssignmentForm = (assignment?: PromotorCategoryAssignmentModel) => {
    this.assignmentForm = assignment ? { ...assignment } : {
      promotorCategoryAssignmentId: null,
      employeeId: this.employee.vIdempleado,
      promoterCategoryId: null,
      assignmentStartDate: null,
      assignmentEndDate: null,
      assignmentReason: '',
      activeId: true
    };
    this.showAssignmentForm = true;
  }
  closeAssignmentForm = () => {
    this.showAssignmentForm = false;
    this.assignmentForm = {};
  }

  assignmentIsValid = (): boolean => {
    let valid = true;
    let errors = [];
    if (!this.assignmentForm.promoterCategoryId) errors.push('Seleccione una categoría.');
    if (!this.assignmentForm.assignmentStartDate) errors.push('Ingrese la fecha de inicio.');
    if (!this.assignmentForm.assignmentEndDate) errors.push('Ingrese la fecha de fin.');
    if (this.assignmentForm.assignmentStartDate && this.assignmentForm.assignmentEndDate
      && new Date(this.assignmentForm.assignmentEndDate) < new Date(this.assignmentForm.assignmentStartDate)) {
      errors.push('La fecha de fin debe ser mayor o igual a la fecha de inicio.');
    }
    if (errors.length > 0) {
      valid = false;
      this.toastr.error(errors.join('<br>'), '¡Atención!', { enableHtml: true });
    }
    return valid;
  }

  saveAssignment = () => {
    if (!this.assignmentIsValid()) return;
    const userName = this.getLoginUserId();
    if (!userName) {
      this.toastr.error('No fue posible identificar al usuario autenticado.');
      return;
    }
    const payload: PromotorCategoryAssignmentModel = {
      promotorCategoryAssignmentId: this.assignmentForm.promotorCategoryAssignmentId || null,
      employeeId: this.employee.vIdempleado,
      promoterCategoryId: this.assignmentForm.promoterCategoryId,
      assignmentStartDate: this.assignmentForm.assignmentStartDate,
      assignmentEndDate: this.assignmentForm.assignmentEndDate,
      assignmentReason: this.assignmentForm.assignmentReason,
      activeId: true
    };
    this.loadingAssignments = true;
    this.assignmentService.saveUpdate(payload, userName)
      .pipe(
        tap((resp: any) => {
          this.loadingAssignments = false;
          if (resp && resp.codeHttpStatus === 200) {
            this.toastr.success('La asignación de categoría se guardó con éxito.');
            this.closeAssignmentForm();
            this.loadAssignments();
          } else {
            this.toastr.error((resp && resp.messageError) || 'No fue posible guardar la asignación.', '¡Atención!');
          }
        }),
        catchError((error: any) => {
          this.loadingAssignments = false;
          const message = error && error.error && error.error.messageError;
          this.toastr.error(message || 'No fue posible guardar la asignación manual.', '¡Atención!');
          return of(null);
        })
      )
      .subscribe();
  }

  deactivateAssignment = (assignment: PromotorCategoryAssignmentModel) => {
    Swal.fire({
      title: '¿Desactivar esta asignación de categoría?',
      html: 'El empleado dejará de tener esta categoría manual asignada a partir de ahora.',
      icon: 'warning',
      showCancelButton: true,
      confirmButtonText: 'Sí, desactivar',
      cancelButtonText: 'Cancelar'
    }).then((result) => {
      if (!result.isConfirmed) return;
      const userName = this.getLoginUserId();
      if (!userName) {
        this.toastr.error('No fue posible identificar al usuario autenticado.');
        return;
      }
      this.loadingAssignments = true;
      this.assignmentService.deactivate(assignment.promotorCategoryAssignmentId, userName)
        .pipe(
          tap((resp: any) => {
            this.loadingAssignments = false;
            if (resp && resp.codeHttpStatus === 200) {
              this.toastr.success('La asignación se desactivó con éxito.');
              this.loadAssignments();
            } else {
              this.toastr.error((resp && resp.messageError) || 'No fue posible desactivar la asignación.', '¡Atención!');
            }
          }),
          catchError((error: any) => {
            this.loadingAssignments = false;
            const message = error && error.error && error.error.messageError;
            this.toastr.error(message || 'No fue posible desactivar la asignación.', '¡Atención!');
            return of(null);
          })
        )
        .subscribe();
    });
  }
}
